import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Aperture, Lightbulb, Layers, Trash2, Camera, ChevronDown, ChevronUp } from "lucide-react";

export default function History() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/suggestions/history").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/suggestions/history/${id}`);
    load();
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen pt-16">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-amber-glow mb-3">Archive</p>
        <h1 className="font-display text-4xl sm:text-5xl mb-10">Suggestion History</h1>

        {items.length === 0 && (
          <p data-testid="history-empty" className="text-neutral-500">No suggestions yet. Generate your first shoot recipe on the Generator.</p>
        )}

        <div className="divide-y divide-white/5">
          {items.map((it, idx) => (
            <motion.div
              key={it._id}
              data-testid="history-row"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
              className="py-5"
            >
              <div className="flex items-center justify-between gap-4">
                <button onClick={() => setOpen(open === it._id ? null : it._id)} className="flex-1 text-left">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                    <span className="flex items-center gap-2 text-white"><Aperture className="h-4 w-4 text-amber-glow" />{it.lens_type}</span>
                    <span className="flex items-center gap-2 text-neutral-300"><Lightbulb className="h-4 w-4 text-neutral-500" />{it.lighting_type}</span>
                    <span className="flex items-center gap-2 text-neutral-300"><Layers className="h-4 w-4 text-neutral-500" />{it.num_lights} lights</span>
                  </div>
                  <p className="text-xs text-neutral-600 mt-1.5">{fmt(it.created_at)}</p>
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpen(open === it._id ? null : it._id)} className="text-neutral-500 hover:text-white p-2">
                    {open === it._id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <button data-testid="delete-history-button" onClick={() => del(it._id)} className="text-neutral-600 hover:text-red-400 p-2">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {open === it._id && it.result && (
                <div className="mt-4 bg-[#121212] border border-white/5 rounded-sm p-6 space-y-4">
                  {it.result.summary && <p className="text-neutral-200">{it.result.summary}</p>}
                  {it.result.camera_settings && (
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      {Object.entries(it.result.camera_settings).map(([k, v]) => (
                        <span key={k} className="text-sm text-neutral-400">
                          <Camera className="inline h-3.5 w-3.5 text-amber-glow mr-1" />
                          <span className="capitalize">{k.replace(/_/g, " ")}:</span> <span className="text-white">{v}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {it.result.lighting_setup && (
                    <ol className="space-y-1.5">
                      {it.result.lighting_setup.map((s, i) => (
                        <li key={i} className="text-sm text-neutral-300"><span className="text-amber-glow mr-2">{i + 1}.</span>{s}</li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
